import React, { useEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { supabase } from './supabaseClient'
import Landing from './pages/Landing'
import Login from './pages/Login'
import Onboarding from './pages/Onboarding'
import Dashboard from './pages/Dashboard'
import Profile from './pages/Profile'
import Configuracion from './pages/Configuracion'
import Admin from './pages/Admin'
import About from './pages/About'
import Feedback from './pages/Feedback'
import Privacidad from './pages/Privacidad'
import Terminos from './pages/Terminos'
import Faqs from './pages/Faqs'
import Cambios from './pages/Cambios'
import BetaConsent, { hasAcceptedRequiredConsents } from './pages/BetaConsent'
import { THEME_OPTIONS, getStoredThemeMode, resolveTheme, persistThemeMode, getThemeColors } from './theme'
const IDLE_TIMEOUT_MS = 25 * 60 * 1000
const IDLE_WARNING_MS = 20 * 60 * 1000
const JOIN_WAITLIST_URL = 'https://fvnuxqgzkeghaimgqqcz.supabase.co/functions/v1/join-waitlist'

function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [pathname])

  return null
}

function BetaWall({ email, theme = THEME_OPTIONS.LIGHT }) {
  const C = getThemeColors(theme)
  const handleSignOut = async () => { await supabase.auth.signOut() }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', system-ui, sans-serif", padding: '2rem' }}>
      <div style={{ background: C.white, borderRadius: '16px', padding: '2.5rem', maxWidth: '420px', width: '100%', textAlign: 'center', border: `0.5px solid ${C.border}`, boxShadow: C.shadow }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>🔒</div>
        <div style={{ fontSize: '20px', fontWeight: '500', color: C.text, marginBottom: '8px', letterSpacing: '-0.5px' }}>
          Aun no tienes acceso
        </div>
        <div style={{ fontSize: '13px', color: C.muted, lineHeight: '1.7', marginBottom: '24px' }}>
          Tu cuenta <strong>{email}</strong> ya esta registrada, pero todavia no tiene acceso activo a la beta de JobPipeline.
        </div>
        <div style={{ background: C.blueLight, borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', fontSize: '12px', color: C.blue, lineHeight: '1.6' }}>
          No necesitas registrarte otra vez.<br />
          Te avisaremos por correo en cuanto tu acceso beta sea habilitado.<br />Si acabas de confirmar tu correo, espera un par de minutos y recarga esta pantalla.
        </div>
        <button
          onClick={handleSignOut}
          style={{ fontSize: '12px', color: C.muted, background: C.bg, border: `0.5px solid ${C.border}`, borderRadius: '8px', padding: '8px 20px', cursor: 'pointer' }}>
          Cerrar sesion
        </button>
      </div>
    </div>
  )
}

function SessionTimeoutModal({ theme = THEME_OPTIONS.LIGHT, secondsLeft = 0, onStay, onLogout }) {
  const C = getThemeColors(theme)

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '16px', boxShadow: C.shadow, padding: '24px', width: '100%', maxWidth: '420px' }}>
        <div style={{ fontSize: '20px', fontWeight: '500', color: C.text, marginBottom: '8px', letterSpacing: '-0.4px' }}>
          Tu sesion esta por expirar
        </div>
        <div style={{ fontSize: '13px', color: C.muted, lineHeight: '1.7', marginBottom: '18px' }}>
          Por seguridad, cerraremos tu sesion por inactividad en aproximadamente <strong>{secondsLeft}</strong> segundos.
        </div>
        <div style={{ background: C.blueLight, color: C.blue, borderRadius: '10px', padding: '12px 14px', fontSize: '12px', lineHeight: '1.6', marginBottom: '18px' }}>
          Si sigues trabajando, puedes mantener tu sesion activa con un clic.
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onLogout} style={{ flex: 1, padding: '11px 14px', background: C.bg, color: C.muted, border: `0.5px solid ${C.border}`, borderRadius: '10px', cursor: 'pointer', fontSize: '13px' }}>
            Cerrar sesion
          </button>
          <button onClick={onStay} style={{ flex: 1.4, padding: '11px 14px', background: C.blue, color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
            Seguir conectado
          </button>
        </div>
      </div>
    </div>
  )
}

function useFeatureFlags(userId) {
  const [flags, setFlags] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }

    const load = async () => {
      try {
        const { data } = await supabase
          .from('feature_flags')
          .select('feature, enabled, user_id')
          .or(`user_id.eq.${userId},user_id.is.null`)

        if (!data) {
          setFlags({})
          return
        }

        const merged = {}
        data.filter((f) => !f.user_id).forEach((f) => { merged[f.feature] = f.enabled })
        data.filter((f) => f.user_id === userId).forEach((f) => { merged[f.feature] = f.enabled })

        setFlags(merged)
      } catch (e) {
        console.warn('[flags]', e)
        setFlags({})
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [userId])

  return { flags, loading }
}

function useProfileBetaAccess(userId) {
  const [betaAccess, setBetaAccess] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) {
      setBetaAccess(null)
      setLoading(false)
      return
    }

    const load = async () => {
      setLoading(true)
      try {
        const { data } = await supabase
          .from('profiles')
          .select('beta_access')
          .eq('id', userId)
          .maybeSingle()

        setBetaAccess(data?.beta_access === true)
      } catch (_error) {
        setBetaAccess(false)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [userId])

  return { betaAccess, loading }
}

function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isRecoveryFlow, setIsRecoveryFlow] = useState(false)
  const [themeMode, setThemeMode] = useState(getStoredThemeMode())
  const [theme, setTheme] = useState(resolveTheme(getStoredThemeMode()))
  const [showIdleWarning, setShowIdleWarning] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(Math.floor((IDLE_TIMEOUT_MS - IDLE_WARNING_MS) / 1000))
  const lastActivityRef = useRef(Date.now())
  const logoutTriggeredRef = useRef(false)

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const query = new URLSearchParams(window.location.search)
    const hasRecoveryParams = hash.get('type') === 'recovery' || query.get('mode') === 'recovery'

    if (hasRecoveryParams) {
      setIsRecoveryFlow(true)
      if (window.location.pathname !== '/login') {
        window.history.replaceState({}, '', '/login?mode=recovery')
      }
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoveryFlow(true)
        window.history.replaceState({}, '', '/login?mode=recovery')
      }

      if (event === 'USER_UPDATED') {
        setIsRecoveryFlow(false)
      }

      setSession(nextSession)
    })

    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!session) {
      setTheme(THEME_OPTIONS.LIGHT)
      return undefined
    }

    const applyTheme = () => setTheme(resolveTheme(themeMode))
    applyTheme()
    persistThemeMode(themeMode)

    if (themeMode !== THEME_OPTIONS.SYSTEM || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const listener = () => applyTheme()
    media.addEventListener('change', listener)
    return () => media.removeEventListener('change', listener)
  }, [themeMode, session])

  useEffect(() => {
    if (!session || isRecoveryFlow) {
      setShowIdleWarning(false)
      setSecondsLeft(Math.floor((IDLE_TIMEOUT_MS - IDLE_WARNING_MS) / 1000))
      logoutTriggeredRef.current = false
      return undefined
    }

    const handleLogout = async () => {
      if (logoutTriggeredRef.current) return
      logoutTriggeredRef.current = true
      setShowIdleWarning(false)
      await supabase.auth.signOut()
    }

    const resetActivity = () => {
      lastActivityRef.current = Date.now()
      logoutTriggeredRef.current = false
      setShowIdleWarning(false)
      setSecondsLeft(Math.floor((IDLE_TIMEOUT_MS - IDLE_WARNING_MS) / 1000))
    }

    const tick = window.setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current
      const warningRemainingMs = IDLE_TIMEOUT_MS - idleMs

      if (idleMs >= IDLE_TIMEOUT_MS) {
        handleLogout()
        return
      }

      if (idleMs >= IDLE_WARNING_MS) {
        setShowIdleWarning(true)
        setSecondsLeft(Math.max(0, Math.ceil(warningRemainingMs / 1000)))
        return
      }

      setShowIdleWarning(false)
      setSecondsLeft(Math.floor((IDLE_TIMEOUT_MS - IDLE_WARNING_MS) / 1000))
    }, 1000)

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click']
    events.forEach((eventName) => window.addEventListener(eventName, resetActivity, { passive: true }))
    resetActivity()

    return () => {
      window.clearInterval(tick)
      events.forEach((eventName) => window.removeEventListener(eventName, resetActivity))
    }
  }, [session, isRecoveryFlow])

  const C = getThemeColors(theme)
  const userId = session?.user?.id ?? null
  const hasLegalConsent = hasAcceptedRequiredConsents(session?.user)
  const { flags, loading: flagsLoading } = useFeatureFlags(userId)
  const { betaAccess: profileBetaAccess, loading: profileBetaLoading } = useProfileBetaAccess(userId)

  useEffect(() => {
    if (!session?.user?.id || !session?.user?.email || flagsLoading || flags === null) return
    if (flags['beta_access'] === true) return

    fetch(JOIN_WAITLIST_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: session.user.id,
        email: session.user.email,
      }),
    }).catch((err) => {
      console.warn('[waitlist] no se pudo registrar usuario pendiente', err)
    })
  }, [session, flags, flagsLoading])

  if (loading || (session && (flagsLoading || profileBetaLoading))) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: C.bg,
        color: C.text,
        fontSize: '14px',
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}>
        Cargando...
      </div>
    )
  }

  const betaFromFlags = flags?.['beta_access'] === true
  const betaFromProfile = profileBetaAccess === true
  const betaGranted = betaFromFlags || betaFromProfile
  const betaBlocked = session && flags !== null && !flagsLoading && !profileBetaLoading && !betaGranted

  const handleStaySignedIn = () => {
    lastActivityRef.current = Date.now()
    logoutTriggeredRef.current = false
    setShowIdleWarning(false)
    setSecondsLeft(Math.floor((IDLE_TIMEOUT_MS - IDLE_WARNING_MS) / 1000))
  }

  const handleManualLogout = async () => {
    setShowIdleWarning(false)
    await supabase.auth.signOut()
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      {showIdleWarning && session && (
        <SessionTimeoutModal
          theme={theme}
          secondsLeft={secondsLeft}
          onStay={handleStaySignedIn}
          onLogout={handleManualLogout}
        />
      )}
      <Routes>
        <Route path="/" element={!session ? <Landing theme={theme} /> : <Navigate to="/dashboard" />} />
        <Route path="/about" element={<About theme={theme} session={session} />} />
        <Route path="/feedback" element={<Feedback theme={theme} session={session} />} />
        <Route path="/cambios" element={<Cambios theme={theme} session={session} />} />
        <Route path="/privacidad" element={<Privacidad theme={theme} session={session} />} />
        <Route path="/terminos" element={<Terminos theme={theme} session={session} />} />
        <Route path="/faqs" element={<Faqs theme={theme} session={session} />} />
        <Route path="/beta-access" element={session ? <BetaConsent theme={theme} /> : <Navigate to="/login" />} />
        <Route path="/login" element={!session || isRecoveryFlow ? <Login theme={theme} /> : <Navigate to="/dashboard" />} />
        <Route path="/onboarding" element={!session ? <Navigate to="/login" /> : betaBlocked ? <BetaWall email={session.user.email} theme={theme} /> : !hasLegalConsent ? <Navigate to="/beta-access" /> : <Onboarding theme={theme} />} />

        <Route
          path="/dashboard"
          element={
            !session
              ? <Navigate to="/login" />
              : isRecoveryFlow
                ? <Navigate to="/login?mode=recovery" />
                : betaBlocked
                  ? <BetaWall email={session.user.email} theme={theme} />
                : !hasLegalConsent
                  ? <Navigate to="/beta-access" />
                  : <Dashboard session={session} flags={flags ?? {}} theme={theme} />
          }
        />

        <Route
          path="/perfil"
          element={
            !session
              ? <Navigate to="/login" />
              : isRecoveryFlow
                ? <Navigate to="/login?mode=recovery" />
                : betaBlocked
                  ? <BetaWall email={session.user.email} theme={theme} />
                : !hasLegalConsent
                  ? <Navigate to="/beta-access" />
                  : <Profile session={session} theme={theme} />
          }
        />

        <Route
          path="/configuracion"
          element={
            !session
              ? <Navigate to="/login" />
              : isRecoveryFlow
                ? <Navigate to="/login?mode=recovery" />
                : betaBlocked
                  ? <BetaWall email={session.user.email} theme={theme} />
                : !hasLegalConsent
                  ? <Navigate to="/beta-access" />
                  : <Configuracion session={session} flags={flags ?? {}} theme={theme} themeMode={themeMode} setThemeMode={setThemeMode} />
          }
        />

        <Route
          path="/admin"
          element={
            !session
              ? <Navigate to="/login" />
              : !hasLegalConsent
                ? <Navigate to="/beta-access" />
                : <Admin session={session} theme={theme} />
          }
        />
      </Routes>
    </BrowserRouter>
  )
}

export default App

